'use client';

import React, { useState } from 'react';
import { ExperienceType } from '../types';
import Itinerary from '@/components/packages/itinerary/Itinerary';
import { ItineraryDayProps, PackageTransfer } from '@/types/itineraryTypes';
import type { Hotel } from '@/components/packages/Hotels';
import type { Vehicle } from '@/components/packages/Vehicles';
import { BackendCurrency } from '@/utils/currencyHelpers';
import { typography } from '@/design-system/tokens/typography';
import Border from '../../common/border';

interface ExperienceItineraryProps {
  experience: unknown;
  type: ExperienceType;
  /** When true, show the itinerary (i) info icon; when false, hide it. Only used for package. */
  showItineraryInfoIcon?: boolean;
  /** When false, hide benefits in itinerary day cards (package only). */
  showBenefits?: boolean;
  /** When true, show hotel section in highlight-card style (package only). */
  showHotelAsHighlights?: boolean;
  /** True when opened from booking flow (?booking=...) */
  isBookingContext?: boolean;
}

interface DayItinerary {
  day?: number;
  title?: string;
  description?: string;
  activities?: string[];
  locations?: string[];
  highlights?: Array<{ icon?: string; label?: string }>;
  relatedExperiences?: Array<{
    image?: string;
    title?: string;
    subtitle?: string;
  }>;
}

export const ExperienceItinerary: React.FC<ExperienceItineraryProps> = ({
  experience,
  type,
  showItineraryInfoIcon,
  showBenefits,
  showHotelAsHighlights,
  isBookingContext = false,
}) => {
  // All hooks must be called at the top level, before any conditional returns
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set());

  if (!experience) return null;

  // For packages, use the existing Itinerary component
  if (type === 'package') {
    const exp = experience as {
      itinerary?: ItineraryDayProps[];
      itineraries?: ItineraryDayProps[];
      currency?: string;
      currencySymbol?: string;
      transfers?: PackageTransfer[];
      airports?: Array<{ _id: string; name?: string; code?: string }>;
      hotels?: Hotel[];
      vehicles?: Vehicle[];
      booking?: { bookingStartDate?: string };
    };
    // Try both 'itinerary' and 'itineraries' field names
    const itineraries = (exp?.itinerary ||
      exp?.itineraries ||
      []) as ItineraryDayProps[];

    if (!Array.isArray(itineraries) || itineraries.length === 0) {
      return null;
    }

    const backendCurrency = (exp?.currency ||
      exp?.currencySymbol ||
      'USD') as BackendCurrency;

    const bookingStartDate = exp?.booking?.bookingStartDate;

    return (
      <Itinerary
        itineraries={itineraries}
        backendCurrency={backendCurrency}
        hotels={exp?.hotels}
        vehicles={exp?.vehicles}
        transfers={exp?.transfers}
        airports={exp?.airports}
        showItineraryInfoIcon={showItineraryInfoIcon}
        showBenefits={showBenefits}
        showHotelAsHighlights={showHotelAsHighlights}
        startDate={bookingStartDate}
        isBookingContext={isBookingContext}
      />
    );
  }

  // For activities and stays, use the simple custom implementation

  const getItinerary = (): DayItinerary[] => {
    const exp = experience as { itinerary?: DayItinerary[] };
    return exp?.itinerary || [];
  };

  const itinerary = getItinerary();

  if (!Array.isArray(itinerary) || itinerary.length === 0) return null;

  const toggleDay = (day: number) => {
    const newExpanded = new Set(expandedDays);
    if (newExpanded.has(day)) {
      newExpanded.delete(day);
    } else {
      newExpanded.add(day);
    }
    setExpandedDays(newExpanded);
  };

  return (
    <div className='space-y-6'>
      <h2
        className='font-semibold text-gray-900'
        style={{
          fontSize: typography.size.lg, // 18px
          fontFamily: typography.fonts.primary,
          lineHeight: 'normal',
        }}
      >
        Itinerary
      </h2>
      <div className='space-y-6'>
        {itinerary.map((day, index) => {
          const dayNumber = day.day ?? index + 1;
          const isExpanded = expandedDays.has(dayNumber);
          const isLast = index === itinerary.length - 1;

          return (
            <div key={index} className='flex gap-4'>
              {/* Day Number Column - Hide for activities on mobile as requested */}
              <div
                className={`flex flex-col items-center ${type === 'activity' ? 'hidden md:flex' : ''}`}
              >
                <div className='flex flex-col items-center'>
                  <span
                    className='text-gray-600 mb-1'
                    style={{
                      fontSize: typography.size.sm, // 14px
                      fontFamily: typography.fonts.primary,
                    }}
                  >
                    Day
                  </span>
                  <div className='w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold'>
                    {dayNumber}
                  </div>
                </div>
                {!isLast && <div className='w-0.5 h-full bg-gray-300 mt-2' />}
              </div>

              {/* Content Column */}
              <div className='flex-1 pb-6'>
                <div className='bg-white border border-gray-200 rounded-lg p-4'>
                  {/* Day Header */}
                  <div className='flex items-center justify-between mb-4'>
                    <div className='flex-1'>
                      {day.locations && day.locations.length > 0 && (
                        <div className='flex items-center gap-2 text-sm text-gray-700 mb-2'>
                          {day.locations.map((location, locIndex) => (
                            <React.Fragment key={locIndex}>
                              <span>{location}</span>
                              {locIndex < day.locations.length - 1 && (
                                <svg
                                  width='16'
                                  height='16'
                                  viewBox='0 0 16 16'
                                  fill='none'
                                  className='text-gray-400'
                                >
                                  <path
                                    d='M6 4l4 4-4 4'
                                    stroke='currentColor'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                  />
                                </svg>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                      {day.title && (
                        <h3 className='text-lg font-semibold text-gray-900'>
                          {day.title}
                        </h3>
                      )}
                    </div>
                    <button
                      onClick={() => toggleDay(dayNumber)}
                      className='p-2 hover:bg-gray-100 rounded-full transition-colors'
                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      <svg
                        width='20'
                        height='20'
                        viewBox='0 0 20 20'
                        fill='none'
                        className={`transition-transform ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      >
                        <path
                          d='M5 7.5l5 5 5-5'
                          stroke='currentColor'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Day Description */}
                  {day.description && (
                    <p className='text-gray-700 mb-4'>{day.description}</p>
                  )}

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className='space-y-4 mt-4'>
                      {/* Activities */}
                      {day.activities && day.activities.length > 0 && (
                        <div>
                          <h4 className='text-sm font-semibold text-gray-900 mb-2'>
                            Activities
                          </h4>
                          <div className='flex flex-wrap gap-2'>
                            {day.activities.map((activity, actIndex) => (
                              <div
                                key={actIndex}
                                className='flex flex-col items-center p-3 bg-gray-50 rounded-lg'
                              >
                                <div className='w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center mb-2'>
                                  <svg
                                    width='20'
                                    height='20'
                                    viewBox='0 0 20 20'
                                    fill='none'
                                  >
                                    <path
                                      d='M10 2.5L12.5 7.5L10 12.5L7.5 7.5L10 2.5Z'
                                      stroke='currentColor'
                                      strokeWidth='1.5'
                                    />
                                  </svg>
                                </div>
                                <span className='text-xs text-gray-700 text-center'>
                                  {activity}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Highlights */}
                      {day.highlights && day.highlights.length > 0 && (
                        <div>
                          <h4 className='text-sm font-semibold text-gray-900 mb-2'>
                            Highlights
                          </h4>
                          <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
                            {day.highlights.map((highlight, hlIndex) => (
                              <div
                                key={hlIndex}
                                className='flex flex-col items-center p-3 bg-gray-50 rounded-lg'
                              >
                                <div className='w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center mb-2'>
                                  <svg
                                    width='20'
                                    height='20'
                                    viewBox='0 0 20 20'
                                    fill='none'
                                  >
                                    <path
                                      d='M10 2.5L12.5 7.5L10 12.5L7.5 7.5L10 2.5Z'
                                      stroke='currentColor'
                                      strokeWidth='1.5'
                                    />
                                  </svg>
                                </div>
                                <span className='text-xs text-gray-700 text-center'>
                                  {highlight.label || highlight.icon}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Related Experiences */}
                      {day.relatedExperiences &&
                        day.relatedExperiences.length > 0 && (
                          <div>
                            <h4 className='text-sm font-semibold text-gray-900 mb-2'>
                              {type === 'activity'
                                ? 'Related Activities'
                                : type === 'stay'
                                  ? 'Related Nexgo Spaces'
                                  : 'Related Experiences'}
                            </h4>
                            <div className='grid grid-cols-3 gap-4'>
                              {day.relatedExperiences.map(
                                (related, relIndex) => (
                                  <div
                                    key={relIndex}
                                    className='bg-gray-50 rounded-lg overflow-hidden'
                                  >
                                    {related.image && (
                                      <div className='w-full h-20 bg-gray-200' />
                                    )}
                                    <div className='p-2'>
                                      <p className='text-xs font-medium text-gray-900'>
                                        {related.title}
                                      </p>
                                      {related.subtitle && (
                                        <div className='flex items-center gap-1 mt-1'>
                                          <svg
                                            width='16'
                                            height='16'
                                            viewBox='0 0 16 16'
                                            fill='none'
                                          >
                                            <path
                                              d='M8 2L10 6L8 10L6 6L8 2Z'
                                              fill='currentColor'
                                            />
                                          </svg>
                                          <span className='text-xs text-gray-600'>
                                            {related.subtitle}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className='pt-8'>
        <Border />
      </div>
    </div>
  );
};
